import React, { createContext, useContext, useState, useEffect } from 'react';
import { get } from '../utils/apiInterceptor';
import { applyHomeScreenIdentity } from '../utils/homeScreenIdentity';
import { isAdminSite } from '../utils/adminSite';
import { rememberSplashLogo } from '../components/Splash';

const DEFAULT_ACCENT = '#7c35e8';

// Mixes a #rrggbb colour towards another (0 = unchanged, 1 = the other colour)
export const mix = (hex, other, amount) => {
  const parse = h => {
    const m = /^#?([0-9a-f]{6})$/i.exec((h || '').trim());
    return m ? [0, 2, 4].map(i => parseInt(m[1].slice(i, i + 2), 16)) : null;
  };
  const a = parse(hex), b = parse(other);
  if (!a || !b) return hex;
  return '#' + a.map((v, i) => Math.round(v + (b[i] - v) * amount).toString(16).padStart(2, '0')).join('');
};

// The --booking-* tokens for a club's branding. Trampoline Life's purple keeps
// the hand-picked darker and lighter shades it was designed with.
export const bookingTokens = (b) => {
  const accent = b.secondaryColor || DEFAULT_ACCENT;
  const isDefault = accent.toLowerCase() === DEFAULT_ACCENT;
  const dark = isDefault ? '#6a1fd4' : mix(accent, '#000000', 0.15);
  const light = isDefault ? '#9b4dca' : mix(accent, '#ffffff', 0.2);
  const tokens = {
    '--booking-accent': accent,
    '--booking-accent-dark': dark,
    '--booking-accent-light': light,
    '--booking-accent-gradient': `linear-gradient(135deg, ${dark}, ${light})`,
  };
  if (b.primaryColor) tokens['--booking-bg-dark'] = b.primaryColor;
  if (b.backgroundColor) tokens['--booking-bg-light'] = b.backgroundColor;
  if (b.textColor) tokens['--booking-text-on-light'] = b.textColor;
  if (b.fontFamily) tokens['--booking-font'] = b.fontFamily;
  return tokens;
};

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  // The site's own name and icon before anyone signs in (Run The Club on its
  // sites); the club's replace them once branding loads
  useEffect(() => {
    try { applyHomeScreenIdentity(); } catch (err) { /* never block the app */ }
  }, []);

  const [branding, setBranding] = useState({
    primaryColor: '#2d2d2d',
    secondaryColor: '#7c35e8',
    accentColor: '#9b4dca',
    backgroundColor: '#eaeaec',
    textColor: '#1a1a1a',
    logoUrl: '',
    fontFamily: "'Exo 2', Arial, sans-serif",
    website: '',
    description: ''
  });
  const [loading, setLoading] = useState(true);

  const fetchBranding = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // Not logged in; keep defaults and avoid noisy 401s
        return;
      }
      const response = await get('/api/branding');
      setBranding(prev => ({ ...prev, ...response.data, clubName: response.data.name }));
      try {
        applyHomeScreenIdentity({ clubName: response.data.name, logoUrl: response.data.logoUrl });
        rememberSplashLogo(response.data.logoUrl);
      } catch (identityErr) {
        console.error('Failed to set Home Screen identity:', identityErr);
      }
    } catch (err) {
      console.error('Failed to fetch branding:', err);
      // Use default values if fetching fails
    } finally {
      setLoading(false);
    }
  };

  const updateBranding = (newBranding) => {
    setBranding(prev => ({ ...prev, ...newBranding }));
  };

  // Apply branding to CSS custom properties
  useEffect(() => {
    const root = document.documentElement;
    
    // Set tracking system CSS custom properties
    root.style.setProperty('--primary-color', branding.primaryColor);
    root.style.setProperty('--secondary-color', branding.secondaryColor);
    root.style.setProperty('--accent-color', branding.accentColor);
    root.style.setProperty('--background-color', branding.backgroundColor);
    root.style.setProperty('--text-color', branding.textColor);

    // Most of the app (the header, menus, buttons, bookings, shop and admin)
    // is styled with the --booking-* tokens, so the club's colours go there too
    for (const [name, value] of Object.entries(bookingTokens(branding))) {
      root.style.setProperty(name, value);
    }

    // The iPhone status bar above the app is painted in the theme colour, so
    // it matches the club's top bar
    // (the admin site's bar is always Run The Club's, set in index.html)
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor && branding.primaryColor && !isAdminSite()) themeColor.setAttribute('content', branding.primaryColor);

    // Font
    if (branding.fontFamily) {
      root.style.setProperty('--font-family', branding.fontFamily);
    }
    
    // Set body background
    document.body.style.backgroundColor = branding.backgroundColor;
    document.body.style.color = branding.textColor;
    
    if (branding.fontFamily) {
      document.body.style.fontFamily = branding.fontFamily;
    }
  }, [branding]);

  useEffect(() => {
    fetchBranding();
  }, []);

  const value = {
    branding,
    updateBranding,
    loading,
    fetchBranding
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}; 